import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppSelector } from '../../hooks/redux';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import { buildAvatarUrl } from '../../utils/imageUrl';
import {
  Home,
  Gavel,
  Video,
  Users,
  MessageCircle,
  ArrowLeftRight,
  User,
  Settings,
  Trophy,
  Wallet,
  TrendingUp,
  Radio,
  Star,
  CreditCard,
  Lock,
  BarChart3,
  UserPlus,
  LayoutDashboard,
  Archive,
  Megaphone,
  Briefcase,
  Sparkles,
  HelpCircle,
  Ticket,
  UserX,
  Gift,
  Search,
} from 'lucide-react';

interface LeftSidebarProps {
  /** When true, renders content only (no outer <aside> wrapper — parent handles layout) */
  embedded?: boolean;
}

const LeftSidebar: React.FC<LeftSidebarProps> = ({ embedded = false }) => {
  const location = useLocation();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const { balance } = useAppSelector((state) => state.wallet);

  const mainNavLinks = [
    { to: '/feed', label: 'Feed', icon: Home },
    { to: '/search', label: 'Search', icon: Search },
    { to: '/game-a-rama', label: 'Game-a-Rama', icon: Gavel },
    { to: '/streams', label: 'Live Streams', icon: Video },
    { to: '/games', label: 'Games', icon: Trophy },
    { to: '/otc', label: 'Token Exchange', icon: ArrowLeftRight },
  ];

  const socialLinks = [
    { to: '/messages', label: 'Messages', icon: MessageCircle },
    { to: '/users', label: 'Discover Users', icon: Users },
    { to: '/trending', label: 'Trending', icon: TrendingUp },
    { to: '/blocked-users', label: 'Blocked Users', icon: UserX },
  ];

  const userLinks = [
    { to: '/profile', label: 'My Profile', icon: User },
    { to: '/dashboard', label: 'Dashboard', icon: Settings },
    { to: '/subscriptions', label: 'My Subscriptions', icon: Star },
    { to: '/game-a-rama/my-wins', label: 'My Wins', icon: Trophy },
    { to: '/wallet', label: 'Wallet', icon: Wallet },
    { to: '/ads', label: 'Advertise', icon: Megaphone },
  ];

  const creatorLinks = [
    { to: '/streaming/archives', label: 'Archived Streams', icon: Archive },
    { to: '/subscriptions/manage', label: 'Subscription Plans', icon: CreditCard },
    { to: '/subscriptions/subscribers', label: 'My Subscribers', icon: Users },
    { to: '/subscriptions/earnings', label: 'Subscription Earnings', icon: BarChart3 },
    { to: '/posts/locked', label: 'Locked Content', icon: Lock },
    { to: '/creator/gifts', label: 'Gift Earnings', icon: Gift },
  ];

  const managerLinks = [
    { to: '/manager/dashboard', label: 'Manager Dashboard', icon: LayoutDashboard },
    { to: '/manager/dashboard?tab=earnings', label: 'My Earnings', icon: BarChart3 },
    { to: '/manager/dashboard?tab=payouts', label: 'Payout Requests', icon: CreditCard },
    { to: '/manager/creators/new', label: 'Create Creator', icon: UserPlus },
  ];

  const isManager = user?.role === 'manager';
  const isCreator = user?.role === 'model';
  const isPlainUser = user?.role === 'user';

  const { creatorApplied, creatorStatus, managerApplied, managerStatus } = useAppSelector(
    (state) => state.applicationStatus
  );
  const unreadCount = useUnreadMessages();
  const showCreatorBtn = !creatorApplied && !(managerApplied && managerStatus !== 'rejected');
  const showManagerBtn = !managerApplied && !(creatorApplied && creatorStatus !== 'rejected');

  const isActiveLink = (path: string) => {
    if (path === '/feed') {
      return location.pathname === '/feed' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const NavLink = ({ to, label, icon: Icon, badge }: { to: string; label: string; icon: React.ElementType; badge?: number }) => {
    const active = isActiveLink(to);
    const hasBadge = !!badge && badge > 0;
    return (
      <Link to={to}>
        <motion.div
          whileHover={{ scale: 1.02, x: 4 }}
          whileTap={{ scale: 0.98 }}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${active
            ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
            : hasBadge
              ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1e2a5e]'
            }`}
        >
          <div className="relative flex-shrink-0">
            <motion.div
              animate={hasBadge && !active ? { rotate: [0, -12, 12, -8, 8, 0] } : {}}
              transition={{ repeat: Infinity, repeatDelay: 4, duration: 0.5 }}
            >
              <Icon className="w-4 h-4" />
            </motion.div>
            {hasBadge && (
              <span className="absolute -top-2 -right-2 min-w-[16px] h-4 bg-emerald-500 text-white rounded-full flex items-center justify-center text-[9px] font-bold px-0.5 animate-pulse shadow-md shadow-emerald-500/40">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </div>
          <span className="truncate flex-1">{label}</span>
          {hasBadge && !active && (
            <span className="flex-shrink-0 text-[10px] font-bold text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded-full">
              New
            </span>
          )}
        </motion.div>
      </Link>
    );
  };

  const content = (
    <div className="space-y-4 pb-4">
      {/* Go Live Button - Only for Creators */}
      {isAuthenticated && isCreator && (
        <Link to="/go-live">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-3.5 shadow-lg text-white"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                <Radio className="w-4 h-4" />
              </div>
              <div>
                <p className="font-bold text-sm">Go Live</p>
                <p className="text-xs text-white/80">Start streaming now</p>
              </div>
            </div>
          </motion.div>
        </Link>
      )}

      {/* User Card */}
      {isAuthenticated && user && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl p-3.5 shadow-lg border border-gray-200 dark:border-[#1e2a5e]"
        >
          <Link to="/profile" className="flex items-center gap-3 mb-3">
            <img
              src={buildAvatarUrl(user.avatar_url) || '/default-avatar.png'}
              alt={user.username}
              className="w-10 h-10 rounded-full border-2 border-emerald-500 object-cover"
            />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 dark:text-white truncate text-sm">{user.username}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
            </div>
          </Link>
          <Link to="/wallet?action=purchase">
            <div className="flex items-center justify-between px-3 py-2 bg-emerald-500/10 rounded-xl">
              <span className="text-xs text-gray-500 dark:text-gray-400">Balance</span>
              <span className="text-sm font-bold text-emerald-500">{balance.toLocaleString()} 🪙</span>
            </div>
          </Link>
        </motion.div>
      )}

      {/* Main Navigation */}
      <div className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl p-2.5 shadow-sm border border-gray-200 dark:border-[#1e2a5e]">
        <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          Main Menu
        </p>
        <nav className="space-y-0.5">
          {mainNavLinks.map((link) => (
            <NavLink key={link.to} {...link} />
          ))}
        </nav>
      </div>

      {/* Social Links */}
      {isAuthenticated && (
        <div className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl p-2.5 shadow-sm border border-gray-200 dark:border-[#1e2a5e]">
          <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Social
          </p>
          <nav className="space-y-0.5">
            {socialLinks.map((link) => (
              <NavLink
                key={link.to}
                {...link}
                badge={link.to === '/messages' ? unreadCount : undefined}
              />
            ))}
          </nav>
        </div>
      )}

      {/* User Links */}
      {isAuthenticated && (
        <div className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl p-2.5 shadow-sm border border-gray-200 dark:border-[#1e2a5e]">
          <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            My Account
          </p>
          <nav className="space-y-0.5">
            {userLinks.map((link) => (
              <NavLink key={link.to} {...link} />
            ))}
          </nav>
        </div>
      )}

      {/* Creator Links */}
      {isAuthenticated && isCreator && (
        <div className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl p-2.5 shadow-sm border border-gray-200 dark:border-[#1e2a5e]">
          <p className="px-3 py-1.5 text-[10px] font-bold text-pink-400 uppercase tracking-widest">
            Creator Tools
          </p>
          <nav className="space-y-0.5">
            {creatorLinks.map((link) => (
              <NavLink key={link.to} {...link} />
            ))}
          </nav>
        </div>
      )}

      {/* Manager Links */}
      {isAuthenticated && isManager && (
        <div className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl p-2.5 shadow-sm border border-gray-200 dark:border-[#1e2a5e]">
          <p className="px-3 py-1.5 text-[10px] font-bold text-emerald-400 uppercase tracking-widest">
            Manager Tools
          </p>
          <nav className="space-y-0.5">
            {managerLinks.map((link) => (
              <NavLink key={link.to} {...link} />
            ))}
          </nav>
        </div>
      )}

      {/* Help & Support */}
      <div className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl p-2.5 shadow-sm border border-gray-200 dark:border-[#1e2a5e]">
        <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          Help & Support
        </p>
        <nav className="space-y-0.5">
          <NavLink to="/help" label="Help Center" icon={HelpCircle} />
          <NavLink to="/faq" label="FAQ" icon={MessageCircle} />
          <NavLink to="/contact" label="Contact Us" icon={Settings} />
          {isAuthenticated && (
            <NavLink to="/support/tickets" label="My Tickets" icon={Ticket} />
          )}
        </nav>
      </div>

      {/* Upgrade Options - only for plain users */}
      {isAuthenticated && isPlainUser && (
        <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-2xl p-3 border border-emerald-500/20">
          <p className="px-1 py-1.5 text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-2">
            Choose Your Role
          </p>
          <p className="text-[10px] text-gray-400 px-1 mb-3">Apply for one role only — Creator or Manager</p>
          <div className="grid grid-cols-2 gap-2">
            {showCreatorBtn && (
              <Link to="/profile">
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-gradient-to-b from-emerald-500 to-teal-600 text-white shadow-md text-center"
                >
                  <Sparkles className="w-5 h-5" />
                  <p className="text-[11px] font-bold leading-tight">Become a Creator</p>
                  <p className="text-[9px] text-white/70">Create games, play games &amp; go live</p>
                </motion.div>
              </Link>
            )}
            {showManagerBtn && (
              <Link to="/profile">
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-gradient-to-b from-teal-500 to-emerald-600 text-white shadow-md text-center"
                >
                  <Briefcase className="w-5 h-5" />
                  <p className="text-[11px] font-bold leading-tight">Become a Manager</p>
                  <p className="text-[9px] text-white/70">Manage creators</p>
                </motion.div>
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // When embedded, just return the content (parent manages the aside + scroll)
  if (embedded) return content;

  // Standalone mode (legacy — sticky scroll)
  return (
    <aside className="w-64 flex-shrink-0 hidden lg:block">
      <div className="sticky top-20 space-y-6 max-h-[calc(100vh-5rem)] overflow-y-auto scrollbar-thin">
        {content}
      </div>
    </aside>
  );
};

export default LeftSidebar;
